let quoteFormCounter = {};
let availableStores = {};

import {mapFieldMutation} from "module/mapField";

import formStructure from './formStructure';

/**
 * Should be used by a quoteForm module in either external or secure to ensure the correct store is used
 */

export default function (store) {
  return class {
    constructor(name) {
      quoteFormCounter[name] = quoteFormCounter[name] ? quoteFormCounter[name] + 1 : 1;

      this.name = name;
      this.storeName = `quoteForm-${name}-${quoteFormCounter[name]}`;

      availableStores[this.storeName] = this;

      //set action names
      this.load = `${this.storeName}/load`;
      this.resetForm = `${this.storeName}/resetForm`;
      this.setSections = `${this.storeName}/setSections`;
      this.setRequired = `${this.storeName}/setRequired`;
      this.resetRequired = `${this.storeName}/resetRequired`;
      this.showMissing = `${this.storeName}/showMissing`;
      this.updateServiceLevel = `${this.storeName}/updateServiceLevel`;
      this.updateBranch = `${this.storeName}/updateBranch`;

      store.registerModule(this.storeName, this.getModule());
    }

    remove() {
      store.unregisterModule(this.storeName);
    }

    getModule() {
      const SET_FORM = `setForm`;
      const SET_SECTIONS = `setSections`;
      const SET_FIELD = `setField`;
      const SET_REQUIRED = `setRequired`;
      const SHOW_MISSING = 'showMissing';

      // initial state
      const state = {
        showMissing: false,
        formSections: Object.keys(formStructure),
        required: ['buildersName', 'companyName', 'companyEmail', 'companyTelephone', 'customerAddressLine1', 'customerAddressLinePostcode'],
        form: _.cloneDeep(formStructure),
        note: null,
        files: {
          logo: [],
          plans: [],
          externalQuotes: []
        }
      };

      //actions
      const actions = {
        load({commit, dispatch}, form) {
          //merge with from structure
          let defaultForm = _.cloneDeep(formStructure);
          commit(SET_FORM, _.merge(defaultForm, form));
          dispatch('resetRequired');
        },
        resetForm({commit, dispatch}) {
          //reset to form structure
          dispatch('resetRequired');
          commit(SET_FORM, _.cloneDeep(formStructure));
        },
        setSections({commit, getters}, sections) {
          commit(SET_SECTIONS, _.cloneDeep(sections));
          commit(SET_FORM, state.form);
        },
        setRequired({commit, getters}, required) {
          let available = Object.keys(getters.missing);
          required.forEach((field) => {
            if (available.indexOf(field) <= -1) {
              throw new Error('Unknown Required Field');
            }
          });
          commit(SET_REQUIRED, required);
        },
        resetRequired({commit, getters}, required) {
          commit(SET_REQUIRED, Object.keys(getters.missing));
        },
        showMissing({commit}, showMissing) {
          commit(SHOW_MISSING, showMissing === true)
        },
        updateServiceLevel({state, commit, rootState}, {path, value}) {
          commit({
            type: SET_FIELD,
            path: 'form.service.quantityTakeOff.serviceLevel',
            value
          });

          let turnaround = null;

          switch (value) {
            case 'Priority':
              turnaround = '2Day';
              break;
            case 'Standard':
              turnaround = '7Day';
              break;
            case 'Extended':
              turnaround = '9Day';
              break;
          }

          commit({
            type: SET_FIELD,
            path: 'form.service.quantityTakeOff.turnaround',
            value: turnaround
          });
        },
        updateBranch({state, commit, rootState}, {path, value, force}) {
          if (state.form.quoteInfo.branch.label !== value || force === true) {
            let defaultBranch = _.cloneDeep(formStructure.quoteInfo.branch);
            let newBranch = rootState.brand.selected.branches.filter(branch => branch.label === value)[0];

            commit({
              type: SET_FIELD,
              path: 'form.quoteInfo.branch',
              value: _.merge(defaultBranch, newBranch)
            });
          }
        }
      };

      //getters
      const getters = {
        getShowMissing(state) {
          return state.showMissing;
        },
        getEmailValidity(state) {
          return state.emailValidity;
        },
        missing: (state, getters, rootState) => {
          return {
            buildersName: state.required.indexOf('buildersName') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.builder.buildersName),
            companyName: state.required.indexOf('companyName') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.builder.name),
            companyEmail: state.required.indexOf('companyEmail') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.builder.email),
            companyTelephone: state.required.indexOf('companyTelephone') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.builder.telephone),
            companyAddressLine1: state.required.indexOf('companyAddressLine1') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.builder.address.line1),
            companyPostcode: state.required.indexOf('companyPostcode') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.builder.address.postcode),
            customerName: state.required.indexOf('customerName') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.customer.name),
            customerAddressLine1: state.required.indexOf('customerAddressLine1') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.customer.address.line1),
            customerAddressLine2: state.required.indexOf('customerAddressLine2') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.customer.address.line2),
            customerAddressLinePostcode: state.required.indexOf('customerAddressLinePostcode') >= 0 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.customer.address.postcode),
            branchNumber: state.required.indexOf('branchNumber') >= 0 && state.form.quoteInfo && (state.form.quoteInfo.branch.number === null || isNaN(state.form.quoteInfo.branch.number) === true),
            branchContact: state.required.indexOf('branchContact') >= 0 && rootState.brand.selected.branches.length >= 1 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.branch.contactName),
            branchEmail: state.required.indexOf('branchEmail') >= 0 && rootState.brand.selected.branches.length >= 1 && state.form.quoteInfo && _.isEmpty(state.form.quoteInfo.branch.email),
            plans: false //state.required.indexOf('plans') >= 0 && state.files.plans.length <= 0
          };
        },
        emailValidity: (state) => {
          return !_.includes(state.form.quoteInfo.builder.email, 'jewson.co.uk');
        },
        isPostcodeValid: (state) => {
          let postcode = state?.form?.quoteInfo?.customer?.address?.postcode;
          postcode = postcode.trim();

          const pattern = /^(GIR 0AA|[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][A-Z]{2})$/i;

          return pattern.test(postcode);
        },
        totalMissing: (state, getters) => {
          return Object.values(getters.missing).filter(v => v === true).length
        },
        getPartialForm: (state) => (sections) => {
          sections = sections || [];

          let partForm = {};

          sections.forEach((section) => {
            partForm[section] = state.form[section];
          });

          return partForm;
        }
      };

      // mutations
      const mutations = {
        ...mapFieldMutation(),
        [SET_FORM](state, newForm) {
          state.form = state.formSections.reduce((form, section) => {
            if (newForm[section]) {
              form[section] = newForm[section];
            }
            return form;
          }, {});
        },
        [SET_SECTIONS](state, sections) {
          state.formSections = sections;
        },
        [SET_REQUIRED](state, required) {
          state.required = required;
        },
        [SHOW_MISSING](state, showMissing) {
          state.showMissing = showMissing;
        }
      };

      return {
        namespaced: true,
        state,
        actions,
        getters,
        mutations
      };
    }
  }
}
